import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePowerSync } from '~/powersync/contexts/power-sync/PowerSync';
import { useLivePowerSyncQuery } from '~/powersync/hooks/useLivePowerSyncQuery';
import { db } from '@/lib/local-api';
import type { ChatMessageRecord } from '~/powersync/generated/schema';
import type { ChatMessage } from '~/chat/hooks/useChatWithPowerSync';

export interface UseContextChatInput {
  /** Routing context for the conversation (e.g. WORKOUT, MEAL). */
  context: string;
  /**
   * Active reference within the context (workout id, or "yyyy-MM-dd" meal day).
   * `null` while the host screen is still resolving it — no chat is created yet.
   */
  contextRefId: string | null;
}

/**
 * Reads/sends messages for a chat scoped to a single `(context, contextRefId)`
 * — the right-rail conversation for a specific workout or meal day. Unlike
 * {@link useChatWithPowerSync} it is not driven by the `/chat/:niceId` route,
 * so opening a workout/meal chat never disturbs the general chat screen. The
 * scoped chat is created lazily the first time the context is known; core-engine
 * routes it to the matching AI graph off the synced `context`/`contextRefId`.
 */
export const useContextChat = ({
  context,
  contextRefId,
}: UseContextChatInput) => {
  const powerSync = usePowerSync();
  const { t } = useTranslation('chat');
  const defaultName = t('list.defaultName');
  const [chatId, setChatId] = useState<string | null>(null);

  // Resolve (or create) the scoped chat once the context reference is known.
  useEffect(() => {
    if (!contextRefId) {
      setChatId(null);
      return;
    }
    let cancelled = false;
    db.Chat.mutations
      .getOrCreateForContext(powerSync, context, contextRefId, defaultName)
      .then((chat) => {
        if (!cancelled) setChatId(chat.id);
      })
      .catch((error) => {
        console.error('Failed to initialize context chat:', error);
      });
    return () => {
      cancelled = true;
    };
  }, [powerSync, context, contextRefId, defaultName]);

  const {
    data: messageRecords,
    isLoading: isLoadingMessages,
    error: messagesError,
  } = useLivePowerSyncQuery<ChatMessageRecord>(
    chatId ? db.Chat.queries.findMessages(chatId) : null,
    [chatId],
  );

  const messages = useMemo<ChatMessage[]>(() => {
    if (!messageRecords) return [];
    return messageRecords.map((record) => {
      const sender = record.sender || '';
      const role: 'user' | 'assistant' =
        sender === 'HUMAN' ? 'user' : 'assistant';
      return {
        id: record.id,
        role,
        sender,
        metadata:
          typeof record.metadata === 'string'
            ? JSON.parse(record.metadata)
            : record.metadata,
        createdAt: record.createdAt ? new Date(record.createdAt) : undefined,
      };
    });
  }, [messageRecords]);

  const saveMessageMutation = useMutation({
    mutationFn: async (text: string) => {
      if (!chatId) throw new Error('Chat not initialized');
      return db.Chat.mutations.createMessage(powerSync, chatId, text);
    },
  });

  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim() || saveMessageMutation.isPending || !chatId) return;
      try {
        await saveMessageMutation.mutateAsync(text.trim());
      } catch (err) {
        console.error('Failed to save message:', err);
        throw err;
      }
    },
    [chatId, saveMessageMutation],
  );

  return {
    messages,
    sendMessage,
    loading: !chatId || isLoadingMessages || saveMessageMutation.isPending,
    // The AI is "thinking" while we're waiting for an assistant reply — i.e. a
    // user message is the last in the list (or one is still being saved). The
    // assistant row arriving flips the last role and clears this.
    thinking:
      saveMessageMutation.isPending ||
      (messages.length > 0 && messages[messages.length - 1].role === 'user'),
    error: messagesError || saveMessageMutation.error,
  };
};
